import type { ApiError } from "../data/errors";
import type { ContactFormValues } from "../contact/form/schema";
import type { ReportFormValues } from "./schema";

/*
 * "Never lose a report" (plan 2D F5): the visit report kept on this device while it is typed and after a failed
 * save. One draft per user + account (workspace) + contact, in localStorage (never in redux-persist): a shared phone
 * never shows one user's draft to another (other users' drafts are removed on load, all drafts on sign-out), and a
 * draft older than 30 days is dropped. Every storage call is wrapped: in private mode the form simply works without.
 */

const PREFIX = "cy.next.reportDraft.";
/** "This report was just saved" (a time), for the other tabs of the app on this device. */
const SAVED_PREFIX = "cy.next.reportSaved.";
/** How long a saved marker is kept (another tab hears of the save at once; this only bounds what is left behind). */
const SAVED_FRESH_MS = 2 * 60 * 1000;
const VERSION = "v1";
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export type ReportDraft = {
  /** The report form. */
  values: ReportFormValues;
  /** "Update contact details" (its own nested form): where it started and what was typed. */
  details?: { start: ContactFormValues; values: ContactFormValues } | null;
  /**
   * The Idempotency-Key of the last attempt and the exact body it went with (both null until a save is tried). A
   * re-send of the same body reuses the key, so a save that did reach the server is never filed twice; a changed
   * body is a new attempt with a new key.
   */
  idempotencyKey: string | null;
  body: string | null;
  /** The route stop it was opened from. */
  pointId?: number;
  /** "Mark this stop as visited" as it was left (absent: on, the default). */
  markStop?: boolean;
  /** ISO time of the last write. */
  savedAt: string;
  /** The last save did not go through: no connection (sent again once when it comes back) or a refusal. */
  failed?: "network" | "refused";
  errorCode?: string;
  /** The answer's status (null: no answer) and the fields it named (server keys), to say and mark it again on Continue. */
  errorStatus?: number | null;
  fieldKeys?: string[];
  /** The one automatic re-send has been made. */
  resent?: boolean;
};

/** No answer (no connection) or a server fault: worth sending again with the same key. A refusal (4xx) is not. */
export const failureKind = (e: ApiError): "network" | "refused" =>
  e.kind === "network" || e.kind === "server" ? "network" : "refused";

/** What a draft keeps of a failed save: how, the code, the status and the fields it named. */
export const failureMark = (e: ApiError): Pick<ReportDraft, "failed" | "errorCode" | "errorStatus" | "fieldKeys"> => {
  const failed = failureKind(e);
  const fieldKeys = e.status === 422 ? Object.keys(e.fieldErrors) : [];
  return {
    failed,
    errorStatus: e.status,
    ...(e.code ? { errorCode: e.code } : {}),
    ...(fieldKeys.length ? { fieldKeys } : {}),
  };
};

export const draftKey = (userId: number, workspaceId: number, householdId: number): string =>
  `${PREFIX}${VERSION}.${userId}.${workspaceId}.${householdId}`;

const storage = (): Storage | null => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
};

const remove = (key: string) => {
  try { storage()?.removeItem(key); } catch { /* private mode: nothing kept */ }
};

const expired = (d: ReportDraft, now = Date.now()) => {
  const at = Date.parse(d.savedAt);
  return Number.isNaN(at) || now - at > MAX_AGE_MS;
};

const parse = (raw: string | null): ReportDraft | null => {
  if (!raw) return null;
  try {
    const d = JSON.parse(raw) as ReportDraft;
    return d && typeof d === "object" && d.values && typeof d.savedAt === "string" ? d : null;
  } catch {
    return null;
  }
};

/** Every report-draft key on this device (or, with `prefix`, every key of that kind). */
const allKeys = (prefix = PREFIX): string[] => {
  const s = storage();
  if (!s) return [];
  try {
    const out: string[] = [];
    for (let i = 0; i < s.length; i += 1) {
      const k = s.key(i);
      if (k?.startsWith(prefix)) out.push(k);
    }
    return out;
  } catch {
    return [];
  }
};

export const saveDraft = (key: string, draft: ReportDraft): boolean => {
  try {
    const s = storage();
    if (!s) return false;
    s.setItem(key, JSON.stringify(draft));
    return true;
  } catch {
    return false; // full or blocked: the form keeps working, only nothing is kept on the device
  }
};

/** The draft, or null (none, unreadable, or older than 30 days — that one is removed). */
export const loadDraft = (key: string): ReportDraft | null => {
  let raw: string | null = null;
  try { raw = storage()?.getItem(key) ?? null; } catch { return null; }
  const d = parse(raw);
  if (raw && (!d || expired(d))) { remove(key); return null; }
  return d;
};

export const clearDraft = (key: string): void => remove(key);

/** The saved marker that goes with a draft key. */
export const reportSavedKey = (key: string): string => SAVED_PREFIX + key.slice(PREFIX.length);

/** A report of this draft key was saved just now (written before the draft is removed). */
export const markReportSaved = (key: string): void => {
  try { storage()?.setItem(reportSavedKey(key), new Date().toISOString()); } catch { /* private mode: other tabs are not told */ }
};

/** When a report of this draft key was last saved (in any tab), or null. */
export const reportSavedAt = (key: string): number | null => {
  let raw: string | null = null;
  try { raw = storage()?.getItem(reportSavedKey(key)) ?? null; } catch { return null; }
  const at = raw ? Date.parse(raw) : NaN;
  return Number.isNaN(at) ? null : at;
};

/** Sign-out: no report draft (nor a saved marker) stays on the device. */
export const clearAllReportDrafts = (): void => { allKeys().forEach(remove); allKeys(SAVED_PREFIX).forEach(remove); };

/** On load: removes every draft of another user (and every expired one), and every saved marker no longer fresh. */
export const purgeForeignDrafts = (userId: number): void => {
  const mine = `${PREFIX}${VERSION}.${userId}.`;
  allKeys().forEach((k) => {
    if (!k.startsWith(mine)) { remove(k); return; }
    loadDraft(k); // drops it when expired or unreadable
  });
  allKeys(SAVED_PREFIX).forEach((k) => {
    const at = reportSavedAt(PREFIX + k.slice(SAVED_PREFIX.length));
    if (at === null || Date.now() - at > SAVED_FRESH_MS) remove(k);
  });
};

/** This user's drafts in this account, with the contact each is for. */
export const listDrafts = (userId: number, workspaceId: number): { key: string; householdId: number; draft: ReportDraft }[] => {
  const prefix = `${PREFIX}${VERSION}.${userId}.${workspaceId}.`;
  return allKeys().flatMap((key) => {
    if (!key.startsWith(prefix)) return [];
    const householdId = Number(key.slice(prefix.length));
    const draft = loadDraft(key);
    return draft && Number.isInteger(householdId) ? [{ key, householdId, draft }] : [];
  });
};

/* ---------- drafts open in a report form right now (the form sends those itself) ---------- */

type Lock = unknown;
type WebLocks = {
  request(name: string, cb: (lock: Lock) => Promise<unknown> | unknown): Promise<unknown>;
  request(name: string, opts: { ifAvailable: boolean }, cb: (lock: Lock | null) => Promise<unknown> | unknown): Promise<unknown>;
};
/** The browser's Web Locks (shared by every tab of the app on this device), or null where there are none. */
const webLocks = (): WebLocks | null => {
  try {
    const l = typeof navigator !== "undefined" ? (navigator as Navigator & { locks?: WebLocks }).locks : undefined;
    return l && typeof l.request === "function" ? l : null;
  } catch {
    return null;
  }
};

const held = new Set<string>();
/**
 * The report form holds this draft while open; returns the release. In this tab a set says so; for the other tabs a
 * Web Lock named after the draft key is held until the release (where the browser has them).
 */
export const holdDraft = (key: string): (() => void) => {
  held.add(key);
  let release: (() => void) | null = null;
  let released = false;
  webLocks()?.request(key, () => new Promise<void>((done) => { if (released) done(); else release = done; })).catch(() => undefined);
  return () => {
    held.delete(key);
    released = true;
    release?.();
  };
};
export const isDraftHeld = (key: string): boolean => held.has(key);

/**
 * Runs `send` only if no form (in any tab) holds the draft, holding its lock until the send is over. Without Web
 * Locks it runs at once (this tab's own check, `isDraftHeld`, still applies).
 */
export const whenDraftFree = (key: string, send: () => Promise<unknown> | void): void => {
  const locks = webLocks();
  if (!locks) { void send(); return; }
  locks.request(key, { ifAvailable: true }, (lock) => (lock ? send() : undefined)).catch(() => undefined);
};

/** Whether this browser shares locks between tabs (without them another tab's form or re-send can't be seen). */
export const hasWebLocks = (): boolean => webLocks() !== null;

/**
 * Calls `free` once no other tab holds this draft (its re-send, or a form open on it): at once when nobody does, else
 * when that tab lets go — also when it was closed or killed without writing anything. The lock is let go at once.
 * Must be asked BEFORE this tab's own form takes the lock (`holdDraft`), or it would wait for itself. Without Web
 * Locks: at once.
 */
export const whenDraftLockFree = (key: string, free: () => void): void => {
  const locks = webLocks();
  if (!locks) { free(); return; }
  locks.request(key, () => { free(); }).catch(() => undefined);
};

/* ---------- drafts being sent again by the app right now (a form opened meanwhile waits) ---------- */

export type SendOutcome = "saved" | "kept";
const sending = new Set<string>();
const sendListeners = new Set<(key: string, outcome: SendOutcome) => void>();
export const markSending = (key: string): void => { sending.add(key); };
export const isDraftSending = (key: string): boolean => sending.has(key);
/** The send is over: "saved" (the draft is gone) or "kept" (it stays, with its new mark). */
export const finishSending = (key: string, outcome: SendOutcome): void => {
  sending.delete(key);
  sendListeners.forEach((l) => l(key, outcome));
};
export const onSendFinished = (listener: (key: string, outcome: SendOutcome) => void): (() => void) => {
  sendListeners.add(listener);
  return () => { sendListeners.delete(listener); };
};
